import { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  Search,
  AlertTriangle,
  Shield,
  FileText,
  BarChart3,
  ClipboardCheck,
  Database,
  Sparkles,
  X,
  Loader2,
  ArrowRight,
} from 'lucide-react'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { ScrollArea } from '@/components/ui/scroll-area'
import { cn } from '@/lib/utils'
import apiClient from '@/lib/api/client'
import { copilotApi, type KnowledgeSearchResult } from '@/services/copilot.service'

interface SearchResult {
  id: string
  title: string
  /** entity_type — semantic results can carry arbitrary types. */
  type: string
  module: string
  href: string
  snippet?: string
  /** 0..1 semantic relevance (undefined for keyword-fallback results). */
  score?: number
}

const TYPE_CONFIG: Record<string, { icon: typeof Search; label: string; color: string }> = {
  risk: { icon: AlertTriangle, label: 'Risque', color: 'text-red-500' },
  control: { icon: Shield, label: 'Controle', color: 'text-blue-500' },
  policy: { icon: FileText, label: 'Politique', color: 'text-purple-500' },
  assessment: { icon: ClipboardCheck, label: 'Evaluation', color: 'text-green-500' },
  evidence: { icon: Database, label: 'Preuve', color: 'text-orange-500' },
  finding: { icon: Search, label: 'Constat', color: 'text-amber-500' },
  framework: { icon: BarChart3, label: 'Referentiel', color: 'text-indigo-500' },
  obligation: { icon: ClipboardCheck, label: 'Obligation', color: 'text-teal-500' },
  incident: { icon: AlertTriangle, label: 'Incident', color: 'text-rose-500' },
}

function typeMeta(type: string) {
  return (
    TYPE_CONFIG[type] ?? {
      icon: Sparkles,
      label: type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Résultat',
      color: 'text-muted-foreground',
    }
  )
}

// ── Semantic search (primary) ─────────────────────────────────────────────────
async function semanticSearch(query: string): Promise<SearchResult[]> {
  const hits: KnowledgeSearchResult[] = await copilotApi.semanticSearch(query, { limit: 15 })
  return hits.map((h) => ({
    id: h.entity_id,
    title: h.title || 'Sans titre',
    type: h.entity_type,
    module: h.entity_type,
    href: h.url || '#',
    snippet: h.snippet,
    score: h.score,
  }))
}

// ── Keyword search (fallback when the semantic endpoint errors / is empty) ─────
async function keywordSearch(query: string): Promise<SearchResult[]> {
  // The api-gateway exposes flat plural namespaces — /risks, /policies, /assessments.
  const [risks, policies, assessments] = await Promise.allSettled([
    apiClient.get(`/risks?search=${encodeURIComponent(query)}&limit=5`).catch(() => ({ data: [] })),
    apiClient.get(`/policies?search=${encodeURIComponent(query)}&limit=5`).catch(() => ({ data: [] })),
    apiClient.get(`/assessments?search=${encodeURIComponent(query)}&limit=5`).catch(() => ({ data: [] })),
  ])

  const results: SearchResult[] = []

  const riskData = risks.status === 'fulfilled' ? (risks.value as any)?.data : []
  if (Array.isArray(riskData)) {
    riskData.forEach((r: any) => {
      results.push({
        id: r.id,
        title: r.name || r.title || 'Sans titre',
        type: 'risk',
        module: 'Risques',
        href: `/risks/${r.id}`,
        snippet: r.description?.slice(0, 100),
      })
    })
  }

  const policyData = policies.status === 'fulfilled' ? (policies.value as any)?.data : []
  if (Array.isArray(policyData)) {
    policyData.forEach((p: any) => {
      results.push({
        id: p.id,
        title: p.title || p.name || 'Sans titre',
        type: 'policy',
        module: 'Gouvernance',
        href: `/governance/policies/${p.id}`,
        snippet: p.description?.slice(0, 100),
      })
    })
  }

  const assessData = assessments.status === 'fulfilled' ? (assessments.value as any)?.data : []
  if (Array.isArray(assessData)) {
    assessData.forEach((a: any) => {
      results.push({
        id: a.id,
        title: a.name || a.title || 'Sans titre',
        type: 'assessment',
        module: 'Evaluations',
        href: `/maturity/${a.id}`,
        snippet: a.description?.slice(0, 100),
      })
    })
  }

  return results
}

/** Semantic first, keyword fallback on error/empty. */
async function runSearch(query: string): Promise<{ results: SearchResult[]; semantic: boolean }> {
  if (!query || query.length < 2) return { results: [], semantic: false }
  try {
    const results = await semanticSearch(query)
    if (results.length > 0) return { results, semantic: true }
  } catch {
    // fall through to keyword
  }
  const results = await keywordSearch(query)
  return { results, semantic: false }
}

export function GlobalSearch({ className }: { className?: string }) {
  const [query, setQuery] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const navigate = useNavigate()
  const containerRef = useRef<HTMLDivElement>(null)

  // Debounce input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedQuery(query), 300)
    return () => clearTimeout(timer)
  }, [query])

  // Query search results
  const { data, isLoading } = useQuery({
    queryKey: ['global-search', debouncedQuery],
    queryFn: () => runSearch(debouncedQuery),
    enabled: debouncedQuery.length >= 2,
    staleTime: 30_000,
  })
  const results = data?.results ?? []
  const isSemantic = data?.semantic ?? false

  // Group by entity_type, preserving relevance order within each group.
  const groups = (() => {
    const map = new Map<string, SearchResult[]>()
    for (const r of results) {
      const arr = map.get(r.type) ?? []
      arr.push(r)
      map.set(r.type, arr)
    }
    return Array.from(map.entries())
  })()

  // Close on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  // Escape closes the results dropdown. NOTE: Ctrl/⌘K is owned by the command
  // palette (see command-palette.tsx) — this input is reached via the `/`
  // shortcut instead (it carries data-search-input), so we must NOT also grab
  // Ctrl+K here or the two would fight over the same chord.
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false)
      }
    }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [])

  const handleSelect = useCallback(
    (result: SearchResult) => {
      if (result.href && result.href !== '#') navigate(result.href)
      setIsOpen(false)
      setQuery('')
    },
    [navigate]
  )

  return (
    <div ref={containerRef} className={cn('relative', className)}>
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          data-search-input
          placeholder="Recherche sémantique...  ( / )"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value)
            setIsOpen(true)
          }}
          onFocus={() => query.length >= 2 && setIsOpen(true)}
          className="pl-10 pr-8 bg-background"
        />
        {query && (
          <button
            onClick={() => {
              setQuery('')
              setIsOpen(false)
            }}
            className="absolute right-3 top-1/2 -translate-y-1/2"
          >
            <X className="h-4 w-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {isOpen && debouncedQuery.length >= 2 && (
        <div className="absolute top-full mt-1 w-full min-w-[400px] z-50 rounded-md border bg-popover shadow-lg">
          <ScrollArea className="max-h-[420px]">
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                <span className="ml-2 text-sm text-muted-foreground">Recherche...</span>
              </div>
            ) : results.length === 0 ? (
              <div className="py-8 text-center text-sm text-muted-foreground">
                Aucun resultat pour &quot;{debouncedQuery}&quot;
              </div>
            ) : (
              <div className="py-1">
                {isSemantic && (
                  <div className="flex items-center gap-1.5 px-3 py-1.5 text-[11px] text-muted-foreground">
                    <Sparkles className="h-3 w-3 text-primary" />
                    Recherche sémantique
                  </div>
                )}
                {groups.map(([type, items]) => {
                  const config = typeMeta(type)
                  const Icon = config.icon
                  return (
                    <div key={type} className="py-1">
                      <p className="px-3 pb-1 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                        {config.label}
                      </p>
                      {items.map((result) => (
                        <button
                          key={`${result.type}-${result.id}`}
                          onClick={() => handleSelect(result)}
                          className="w-full flex items-start gap-3 px-3 py-2.5 hover:bg-accent text-left transition-colors"
                        >
                          <Icon className={cn('h-4 w-4 mt-0.5 shrink-0', config.color)} />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <span className="text-sm font-medium truncate">{result.title}</span>
                              {typeof result.score === 'number' && (
                                <Badge variant="outline" className="text-[10px] shrink-0">
                                  {Math.round(result.score * 100)}%
                                </Badge>
                              )}
                            </div>
                            {result.snippet && (
                              <p className="text-xs text-muted-foreground truncate mt-0.5">
                                {result.snippet}
                              </p>
                            )}
                          </div>
                          <ArrowRight className="h-3 w-3 text-muted-foreground mt-1 shrink-0" />
                        </button>
                      ))}
                    </div>
                  )
                })}
              </div>
            )}
          </ScrollArea>
        </div>
      )}
    </div>
  )
}
